import { useEffect, useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { useIsAdminTier, useIsSuperAdmin } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { ErrorDialog, SuccessDialog } from '@/shared/components/ui/status-dialog';
import { ConfiguracionFilterPopover } from './ConfiguracionFilterPopover';
import { CONFIG_ENTITIES, type ConfigEntityKey } from './constants';
import type { EntityStatus, EntityViewProps } from './types';
import { UsuariosEntity } from './entities/usuarios/UsuariosEntity';
import { MediosDeContactoEntity } from './entities/medios-contacto/MediosDeContactoEntity';
import { CategoriasEntity } from './entities/categorias/CategoriasEntity';
import { PercepcionesImpuestoEntity } from './entities/percepciones-impuesto/PercepcionesImpuestoEntity';
import { CuentasContablesEntity } from './entities/cuentas-contables/CuentasContablesEntity';
import { ParametrosEntity } from './entities/parametros/ParametrosEntity';
import { ParametrosSistemaEntity } from './entities/parametros-sistema/ParametrosSistemaEntity';
import { ProductosEntity } from './entities/articulos/ProductosEntity';
import { ServiciosEntity } from './entities/articulos/ServiciosEntity';
import { ProveedoresEntity } from './entities/proveedores/ProveedoresEntity';
import { CampaniasEntity } from './entities/campanias/CampaniasEntity';
import { MediosDePagoEntity } from './entities/medios-de-pago/MediosDePagoEntity';
import { PuntosVentaEntity } from './entities/puntos-venta/PuntosVentaEntity';
import { EfItemsEntity } from './entities/estado-financiero/EfItemsEntity';
import { EfSubitemsEntity } from './entities/estado-financiero/EfSubitemsEntity';
import { CaucionTasasEntity } from './entities/estado-financiero/CaucionTasasEntity';
import { CuotasMercadoPagoEntity } from './entities/cuotas-mercadopago/CuotasMercadoPagoEntity';

export function ConfiguracionPage() {
  // Configuración writes are admin-only. Other roles (administracion
  // included) see the master data read-only — no "+ Nuevo" button at
  // the page level, no Pencil/Trash on rows (gated inside each
  // entity component via the same hook). Backend pairs this with a
  // method-level guard so the API rejects writes from non-admins
  // even if the UI gate is bypassed.
  const canEdit = useIsAdminTier();
  // `admin` only — the accounting taxonomy (Categorías, Cuentas Contables) and
  // EF Items/SubItems are hidden from `administracion`.
  const isSuperAdmin = useIsSuperAdmin();
  const { config: activeCountry } = useActiveCountry();
  const [entityKey, setEntityKey] = useState<ConfigEntityKey>('usuarios');
  const [search, setSearch] = useState('');
  const [openCreate, setOpenCreate] = useState(false);
  const [status, setStatus] = useState<EntityStatus | null>(null);
  const [filterValue, setFilterValue] = useState<string | null>(null);
  const [secondaryFilterValue, setSecondaryFilterValue] = useState<string | null>(
    () => {
      const initial = CONFIG_ENTITIES.find((e) => e.key === 'usuarios');
      return initial && 'secondaryFilter' in initial && initial.secondaryFilter
        ? initial.secondaryFilter.defaultValue
        : null;
    },
  );

  // Display order: A→Z by label, locale-aware ('es') so accented
  // letters (Categorías, Campañas) sort correctly. The source array
  // intentionally keeps the legacy PowerApps order for code-history
  // continuity, so we only re-sort at render time.
  const sortedEntities = useMemo(
    () =>
      [...CONFIG_ENTITIES]
        // `arOnly` entities (EF Items / SubItems) are hidden outside AR.
        .filter((e) => !('arOnly' in e && e.arOnly) || activeCountry.code === 'AR')
        // `adminOnly` entities (Categorías, Cuentas Contables, EF Items/SubItems)
        // are hidden from `administracion` — only `admin` sees them.
        .filter((e) => !('adminOnly' in e && e.adminOnly) || isSuperAdmin)
        .sort((a, b) => a.label.localeCompare(b.label, 'es')),
    [activeCountry.code, isSuperAdmin],
  );

  // If the active country switches away from one that exposes the current
  // entity (e.g. sitting on EF Items, then switching to MX), fall back to a
  // always-visible entity so the page doesn't render a hidden selection.
  useEffect(() => {
    if (!sortedEntities.some((e) => e.key === entityKey)) {
      setEntityKey('usuarios');
    }
  }, [sortedEntities, entityKey]);

  const entity = CONFIG_ENTITIES.find((e) => e.key === entityKey)!;
  const nuevoLabel = `${'femenino' in entity ? 'Nueva' : 'Nuevo'} ${entity.singular}`;
  const filterSpec = 'filter' in entity ? entity.filter : null;
  const secondaryFilterSpec =
    'secondaryFilter' in entity && entity.secondaryFilter ? entity.secondaryFilter : null;

  const onEntityChange = (key: ConfigEntityKey) => {
    setEntityKey(key);
    setSearch('');
    setOpenCreate(false);
    const next = CONFIG_ENTITIES.find((e) => e.key === key);
    setFilterValue(next && 'filter' in next ? next.filter.defaultValue : null);
    setSecondaryFilterValue(
      next && 'secondaryFilter' in next && next.secondaryFilter
        ? next.secondaryFilter.defaultValue
        : null,
    );
  };

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Toolbar — DOM order matches desktop (Title, +, Entity Select,
          Search, Filter). On mobile a `basis-full` break drops Search +
          Filter onto a second row, leaving the title row with [Title ·
          + · Entity Select]. The `– Entity` suffix is hidden on mobile
          since the entity is already shown in the select. */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Configuración{' '}
          <span className="hidden text-muted-foreground sm:inline">– {entity.label}</span>
        </h1>
        {/*
         * Parámetros (Comisiones/Descuentos) and Parámetros del Sistema
         * are edit-only — the list is seeded and rows are not created
         * through the UI. Hide the + button instead of showing a useless
         * toast when clicked.
         */}
        {entity.key !== 'parametros' && entity.key !== 'parametros_sistema' && canEdit && (
          <Button
            size="icon"
            className="h-12 w-12 shrink-0 bg-foreground text-background hover:bg-foreground/90"
            onClick={() => setOpenCreate(true)}
            aria-label={nuevoLabel}
            title={nuevoLabel}
          >
            <Plus className="h-4 w-4" aria-hidden />
          </Button>
        )}
        <div className="w-full sm:w-48">
          <Select value={entityKey} onValueChange={(v) => onEntityChange(v as ConfigEntityKey)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortedEntities.map((e) => (
                <SelectItem key={e.key} value={e.key}>
                  {e.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Forces search + filter onto a new row on mobile only. */}
        <div className="basis-full sm:hidden" aria-hidden />
        <div className="relative min-w-[8rem] flex-1 sm:flex-initial sm:basis-64">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            variant="filled"
            placeholder="Búsqueda"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar"
          />
        </div>
        {(filterSpec || secondaryFilterSpec) && (
          <ConfiguracionFilterPopover
            primary={filterSpec}
            primaryValue={filterValue}
            onPrimaryChange={setFilterValue}
            secondary={secondaryFilterSpec}
            secondaryValue={secondaryFilterValue}
            onSecondaryChange={setSecondaryFilterValue}
          />
        )}
      </div>

      <div className="min-h-0 flex-1">
        <EntityView
          entityKey={entityKey}
          entityLabel={entity.label}
          search={search}
          filterValue={filterValue}
          secondaryFilterValue={secondaryFilterValue}
          onStatus={setStatus}
          openCreate={openCreate}
          onCreateHandled={() => setOpenCreate(false)}
        />
      </div>


      <SuccessDialog
        open={status?.kind === 'success'}
        message={status?.kind === 'success' ? status.message : ''}
        onClose={() => setStatus(null)}
      />
      <ErrorDialog
        open={status?.kind === 'error'}
        message={status?.kind === 'error' ? status.message : ''}
        onClose={() => setStatus(null)}
      />
    </div>
  );
}

function EntityView({
  entityKey,
  entityLabel,
  ...rest
}: EntityViewProps & { entityKey: ConfigEntityKey; entityLabel: string }) {
  switch (entityKey) {
    case 'usuarios':
      return <UsuariosEntity {...rest} />;
    case 'medios_contacto':
      return <MediosDeContactoEntity {...rest} />;
    case 'categorias':
      return <CategoriasEntity {...rest} />;
    case 'percepciones':
      return <PercepcionesImpuestoEntity {...rest} />;
    case 'cuentas_contables':
      return <CuentasContablesEntity {...rest} />;
    case 'parametros':
      return <ParametrosEntity {...rest} />;
    case 'parametros_sistema':
      return <ParametrosSistemaEntity {...rest} />;
    case 'productos':
      return <ProductosEntity {...rest} />;
    case 'servicios':
      return <ServiciosEntity {...rest} />;
    case 'proveedores':
      return <ProveedoresEntity {...rest} />;
    case 'campanias':
      return <CampaniasEntity {...rest} />;
    case 'medios_pago':
      return <MediosDePagoEntity {...rest} />;
    case 'puntos_venta':
      return <PuntosVentaEntity {...rest} />;
    case 'ef_items':
      return <EfItemsEntity {...rest} />;
    case 'ef_subitems':
      return <EfSubitemsEntity {...rest} />;
    case 'caucion_tasas':
      return <CaucionTasasEntity {...rest} />;
    case 'mp_cuotas':
      return <CuotasMercadoPagoEntity {...rest} />;
    default:
      return <ComingSoon entityLabel={entityLabel} />;
  }
}

function ComingSoon({ entityLabel }: { entityLabel: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed bg-card/40 px-6 py-20 text-center">
      <p className="text-base font-medium text-foreground">{entityLabel}</p>
      <p className="max-w-sm text-sm text-muted-foreground">
        Este CRUD se portará en una próxima iteración del módulo de Configuración.
      </p>
    </div>
  );
}
